import { AppRoutes, Layout, useAppDispatch } from '@core';
import { SeatSelection } from '@modules-seatSelection';
import { DateSelection } from '@modules-dateSelection';
import { Checkout } from '@modules-checkout';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { setUrlParamsData } from './modules/seatSelection/store/slice';
import { resolvePartnerSite, resolveThemeId } from './core/utils/resolvePartnerSite';
import { useWidgetHandshake } from './core/hooks/useWidgetHandshake';
import { Loader } from './shared/Loader/Loader';

function App() {
  const dispatch = useAppDispatch();
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  // Query сообщает ожидаемый origin родителя; доверенным он становится только после handshake.
  const origin = searchParams.get('origin') ?? undefined;
  const partnerSite = useMemo(() => resolvePartnerSite(origin), [origin]);
  const themeId = useMemo(() => resolveThemeId(origin), [origin]);
  const isOriginAllowed = Boolean(partnerSite);
  const showIds = searchParams.get('showIds') ?? searchParams.get('showId') ?? undefined;
  const eventsIds = searchParams.get('eventsIds') ?? searchParams.get('eventId') ?? undefined;
  const hasShowIds = Boolean(showIds);
  const hasEventIds = Boolean(eventsIds);
  const handshakeStatus = useWidgetHandshake({
    enabled: Boolean(partnerSite && hasShowIds),
    expectedParentOrigin: partnerSite ? origin : undefined,
  });

  useEffect(() => {
    document.documentElement.dataset.theme = themeId;

    return () => {
      delete document.documentElement.dataset.theme;
    };
  }, [themeId]);

  useEffect(() => {
    if (!isOriginAllowed || handshakeStatus !== 'authorized') return;

    dispatch(setUrlParamsData({ showIds, eventsIds }));
  }, [dispatch, showIds, eventsIds, handshakeStatus, isOriginAllowed]);

  if (!partnerSite || !hasShowIds || handshakeStatus === 'denied') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page">
        <p className="px-4 text-center text-lg text-status-error">Доступ ограничен.</p>
      </div>
    );
  }

  if (handshakeStatus === 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page">
        <Loader />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout themeId={themeId} />}>
          <Route path={AppRoutes.DATE_SELECTION} element={<DateSelection />} />
          <Route
            path={AppRoutes.SEATS_SELECTION}
            element={<SeatSelection />}
          />
          <Route
            path={AppRoutes.CHECKOUT}
            element={<Checkout platformType={partnerSite.platformType} />}
          />
        </Route>
        <Route
          path="/"
          element={
            <Navigate
              to={hasEventIds ? AppRoutes.SEATS_SELECTION : AppRoutes.DATE_SELECTION}
              replace
            />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
